import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import { isExactMapMode, selectMapMode } from 'client/store/mapSlice/mapSlice';
import { selectDrawType } from 'client/store/drawSlice/drawSlice';
import { mapModes } from 'client/utils/constants/appStates';
import { LINE } from 'client/utils/constants/symbolName';

const INITIAL_MAP_STATE = [mapModes.DEFAULT, mapModes.AOI];
const LOADING_MAP_STATE = [mapModes.DEFAULT, mapModes.LOADING];

export const useSnackBarState = () => {
    const location = useLocation();
    const aoiMode = useSelector(selectMapMode);
    const drawType = useSelector(selectDrawType);

    const hasBookmarkParam = new URLSearchParams(location.search).has('bookmark');
    const [hasInteractedWithBookmarkAoi, setHasInteractedWithBookmarkAoi] = useState(false);
    const [suppressBookmarkLoading, setSuppressBookmarkLoading] = useState(
        hasBookmarkParam && (location.key !== 'default' || isExactMapMode(aoiMode, LOADING_MAP_STATE))
    );
    const [isDismissed, setIsDismissed] = useState(false);
    const wasInActionRef = useRef(false);
    const wasOpenBeforeLoadingRef = useRef(false);

    const isInitialAoiMode = isExactMapMode(aoiMode, INITIAL_MAP_STATE);
    const isLoading = aoiMode.includes(mapModes.LOADING);
    const isBookmarkLoading = LOADING_MAP_STATE.every(mode => aoiMode.includes(mode));
    const isAoiActionMode = aoiMode.includes(mapModes.DRAWING) || aoiMode.includes(mapModes.MODIFYING);
    const isInAction = drawType === LINE && isAoiActionMode;

    // Keep bookmark suppression scoped to initial bookmark load only.
    useEffect(() => {
        if (!hasBookmarkParam) {
            setHasInteractedWithBookmarkAoi(false);
            setSuppressBookmarkLoading(false);
            return;
        }

        if (isAoiActionMode) {
            setHasInteractedWithBookmarkAoi(true);
        }

        if (suppressBookmarkLoading && !isLoading) {
            setSuppressBookmarkLoading(false);
        }
    }, [hasBookmarkParam, isAoiActionMode, suppressBookmarkLoading, isLoading]);

    // A new line draw/modify action should re-open the banner if it was dismissed.
    useEffect(() => {
        if (isInAction && !wasInActionRef.current) {
            setIsDismissed(false);
        }

        wasInActionRef.current = isInAction;
    }, [isInAction]);

    // bookmark is supressed if:
    // - there is a bookmark param
    // - the user has not interacted with the AOI since loading the bookmark
    // - the map is in the initial AOI mode or is currently loading the bookmark
    const isBookmarkSuppressed = hasBookmarkParam && !hasInteractedWithBookmarkAoi && (
        isInitialAoiMode ||
        (suppressBookmarkLoading && isBookmarkLoading)
    );

    // only show banner if:
    // - drawing a line
    // - not suppressed by bookmark loading state
    // - not already dismissed
    // - not in default-only mode
    const shouldShowBanner = drawType === LINE && !isBookmarkSuppressed && !isDismissed && !(
        aoiMode.length === 1 && aoiMode.includes(mapModes.DEFAULT)
    );

    // Freeze visibility while loading:
    // open before, open after
    // closed before, closed after
    useEffect(() => {
        if (!isLoading) {
            wasOpenBeforeLoadingRef.current = shouldShowBanner;
        }
    }, [isLoading, shouldShowBanner]);

    const isOpen = isLoading
        ? wasOpenBeforeLoadingRef.current
        : shouldShowBanner;

    const dismiss = useCallback(() => {
        setIsDismissed(true);
    }, []);

    return {
        isOpen,
        dismiss
    };
};
